import type { TelephoneFieldValue } from "@jti/commerce-types";

const telephoneFieldValueToStringAdapter = (telephone: TelephoneFieldValue): string => {
  if (!telephone) {
    return "";
  }

  if (telephone.internationalPhone) {
    return telephone.internationalPhone;
  }

  if (telephone.code && telephone.phone) {
    return `${telephone.code.value}${telephone.phone}`;
  }

  return "";
};

/**
 * Converts a telephone field value to a string, with optional fallback.
 *
 * When a fallback is provided, this handles the case where the "Populated phone input" option
 * is disabled in checkout configuration. In that scenario, the phone input field is hidden and
 * its form value is empty, but the API still requires a valid phone number. The fallback
 * (typically from user profile) ensures the API request includes a phone number.
 *
 * @param telephone - The primary telephone field value (from form state)
 * @param fallback - Optional fallback telephone field value (typically from user profile)
 * @returns The phone number string from telephone, or from fallback if telephone produces an empty string
 */
export const toTelephoneString = (telephone: TelephoneFieldValue, fallback?: TelephoneFieldValue): string => {
  const result = telephoneFieldValueToStringAdapter(telephone);

  if (result || !fallback) {
    return result;
  }

  return telephoneFieldValueToStringAdapter(fallback);
};
